import { createContext, useContext, useState, type ReactNode } from "react";
import OnboardingOverlay from "../components/Onboarding/OnboardingOverlay";
import { fetchOnboardingInitialData, type OnboardingFormData } from "../api/getOnboardingInitialData";
import { useDispatch } from "react-redux";
import { openAuthOverlay } from "../components/redux/AuthActions";

type OnboardingContextType = {
  openOnboarding: (token: string) => void;
  closeOnboarding: () => void;
};

export type LoginAfterRegistration = (email: string, password: string) => Promise<boolean>;

const OnboardingOverlayContext = createContext<OnboardingContextType | null>(null);

interface Props {
  children: ReactNode;
  loginAfterRegistration: LoginAfterRegistration;
  loginErrorComponent?: ReactNode;
}

export const OnboardingOverlayProvider = ({ children, loginAfterRegistration, loginErrorComponent }: Props) => {
  const [openOnboarding, setOpenOnboarding] = useState(false);
  const [onboardingData, setOnboardingData] = useState<OnboardingFormData | null>(null);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const loadOnboardingData = async (token: string) => {
    setLoading(true);

    try {
      const { data } = await fetchOnboardingInitialData(token);

      setOnboardingData(data);
      setOpenOnboarding(true);
    } finally {
      setLoading(false);
    }
  };

  const onSignUpClick = () => {
    setOpenOnboarding(false);
    dispatch(openAuthOverlay("register"));
  };

  return (
    <OnboardingOverlayContext.Provider
      value={{
        openOnboarding: async (token: string) => await loadOnboardingData(token),
        closeOnboarding: () => setOpenOnboarding(false),
      }}
    >
      {children}

      {openOnboarding && !loading && onboardingData != null && (
        <OnboardingOverlay
          data={onboardingData}
          loginAfterRegistration={loginAfterRegistration}
          loginErrorComponent={loginErrorComponent}
          onSignUpClick={onSignUpClick}
        />
      )}
    </OnboardingOverlayContext.Provider>
  );
};

export const useOnboardingOverlay = () => {
  const context = useContext(OnboardingOverlayContext);

  if (!context) {
    throw new Error("useOnboardingOverlay must be used inside OnboardingOverlayProvider");
  }

  return context;
};
